import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { QrCode, LayoutDashboard, Trophy, LogOut } from 'lucide-react';
import { AuthProvider, useAuth } from './components/auth/AuthProvider';
import LandingPage from './components/LandingPage';
import AdminDashboard from './components/AdminDashboard';
import RaffleEntry from './components/RaffleEntry';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import CampaignManager from './components/campaign/CampaignManager';
import TermsAndConditions from './components/TermsAndConditions';
import Payment from './components/payment/Payment';
import Success from './components/payment/Success';
import CheckoutPage from './components/payment/CheckoutPage';
import CheckoutSuccess from './components/payment/CheckoutSuccess';
import OrganizationOnboarding from './components/onboarding/OrganizationOnboarding';
import ProceedsDistribution from './components/admin/ProceedsDistribution';
import { supabase } from './lib/supabase';
import AnalyticsDashboard from './components/analytics/AnalyticsDashboard';
import PublicRaffleLayout from './components/layouts/PublicRaffleLayout';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

const OnboardingRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [hasOrganization, setHasOrganization] = useState(false);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user) {
        setChecking(false);
        return;
      }

      try {
        const { data: orgs, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('owner_id', user.id)
          .limit(1);

        if (orgError) throw orgError;

        // Check if organization exists and is compliant
        const isCompliant = orgs && orgs.length > 0 &&
          orgs[0].organization_type &&
          orgs[0].organization_type !== 'other' &&
          orgs[0].organization_age >= 1 &&
          orgs[0].tax_id &&
          orgs[0].state_registration;

        setHasOrganization(isCompliant);
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      } finally {
        setChecking(false);
      }
    };

    checkOnboardingStatus();
  }, [user]);

  if (loading || checking) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (hasOrganization) {
    return <Navigate to="/campaigns" />;
  }

  return <>{children}</>;
};

const Navigation = () => {
  const { user, signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <nav className="bg-white/10 backdrop-blur-md border-b border-white/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <QrCode className="h-8 w-8 text-white" />
              <span className="ml-2 text-xl font-bold text-white">QRaffle</span>
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            {user ? (
              <>
                <Link
                  to="/campaigns"
                  className="text-white hover:text-white/80 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <Trophy className="h-5 w-5 mr-2" />
                  Raffles
                </Link>
                <Link
                  to="/admin"
                  className="text-white hover:text-white/80 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <LayoutDashboard className="h-5 w-5 mr-2" />
                  Dashboard
                </Link>
                <Link
                  to="/analytics"
                  className="text-white hover:text-white/80 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <LayoutDashboard className="h-5 w-5 mr-2" />
                  Analytics
                </Link>
                <button
                  onClick={handleSignOut}
                  className="text-white hover:text-white/80 px-3 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-white hover:text-white/80 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="bg-white text-purple-600 hover:bg-white/90 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Get Started
                </Link>
              </>
            )}
            <Link
              to="/terms"
              className="text-white hover:text-white/80 px-3 py-2 rounded-md text-sm font-medium"
            >
              Terms
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public raffle entry route with custom layout */}
          <Route path="/entry/:eventId" element={
            <PublicRaffleLayout>
              <RaffleEntry />
            </PublicRaffleLayout>
          } />

          {/* All other routes with standard layout */}
          <Route path="*" element={
            <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-500">
              <Navigation />
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/terms" element={<TermsAndConditions />} />
                  <Route path="/onboarding" element={
                    <OnboardingRoute>
                      <OrganizationOnboarding />
                    </OnboardingRoute>
                  } />
                  <Route path="/admin" element={
                    <PrivateRoute>
                      <AdminDashboard />
                    </PrivateRoute>
                  } />
                  <Route path="/analytics" element={
                    <PrivateRoute>
                      <AnalyticsDashboard />
                    </PrivateRoute>
                  } />
                  <Route path="/campaigns" element={
                    <PrivateRoute>
                      <CampaignManager />
                    </PrivateRoute>
                  } />
                  <Route path="/payment/:entryId" element={<Payment />} />
                  <Route path="/success/:ticketNumber" element={<Success />} />
                  <Route path="/checkout/:campaignId" element={<CheckoutPage />} />
                  <Route path="/checkout/success/:entryId" element={<CheckoutSuccess />} />
                  <Route path="/admin/proceeds/:campaignId" element={
                    <PrivateRoute>
                      <ProceedsDistribution />
                    </PrivateRoute>
                  } />
                </Routes>
              </div>
            </div>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;